import { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/Auth";
import { IDLE_TIMEOUT_MS, WARNING_BEFORE_MS } from "@/config/session";

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "wheel",
  "touchstart",
  "pointerdown",
  "input",
  "change",
];

/**
 * Tracks user inactivity and triggers logout after the configured timeout.
 * Shows a warning modal before logging out.
 *
 * Returns { showWarning, dismissWarning, logoutNow }
 */
export function useIdleLogout() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showWarning, setShowWarning] = useState(false);

  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    warningTimer.current = null;
    logoutTimer.current = null;
  }, []);

  const logoutNow = useCallback(async () => {
    if (import.meta.env.DEV) console.log("Auto logout triggered");
    clearTimers();
    setShowWarning(false);

    // Clean up local storage but preserve questionnaire autosave
    const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || "")
      .replace("https://", "")
      .replace(".supabase.co", "");
    localStorage.removeItem("supabase.auth.token");
    localStorage.removeItem(`sb-${supabaseUrl}-auth-token`);

    await supabase.auth.signOut();
    navigate("/login");
  }, [clearTimers, navigate]);

  const startTimers = useCallback(() => {
    if (import.meta.env.DEV) console.log("Resetting inactivity timer", new Date().toISOString());
    clearTimers();
    setShowWarning(false);

    // Timer to show warning
    warningTimer.current = setTimeout(() => {
      if (import.meta.env.DEV) console.log("Warning timer fired — showing idle warning");
      setShowWarning(true);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Timer to actually log out
    logoutTimer.current = setTimeout(() => {
      if (import.meta.env.DEV) console.log("Inactivity timeout reached - logging out");
      logoutNow();
    }, IDLE_TIMEOUT_MS);
  }, [clearTimers, logoutNow]);

  const dismissWarning = useCallback(() => {
    // User clicked "Stay Signed In" — reset everything
    startTimers();
  }, [startTimers]);

  // Track showWarning in a ref so the activity handler can read it
  // without being in the effect's dependency array
  const showWarningRef = useRef(showWarning);
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // Reset timers on user activity
  useEffect(() => {
    if (!session) return;

    // Throttle: ignore activity if last reset was < 1s ago
    let lastReset = Date.now();
    const throttledHandler = () => {
      const now = Date.now();
      if (now - lastReset < 1_000) return;
      // Don't reset if warning is showing (user must click dismiss)
      if (showWarningRef.current) return;
      lastReset = now;
      if (import.meta.env.DEV) console.log("User activity detected");
      startTimers();
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, throttledHandler, { passive: true })
    );

    // Start on mount
    startTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, throttledHandler)
      );
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Reset timers on route change
  useEffect(() => {
    if (session && !showWarning) {
      startTimers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // No session → no warning
  useEffect(() => {
    if (!session) {
      clearTimers();
      setShowWarning(false);
    }
  }, [session, clearTimers]);

  return { showWarning, dismissWarning, logoutNow };
}
